import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Select,
  Skeleton,
  Text,
  useToast,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import React, { useCallback, useEffect, useState } from 'react';
import ReactSelect, { MultiValue } from 'react-select';
import { useNavigate, useParams } from 'react-router-dom';
import { addPlanToStates } from '../../../services/plans/addPlanToStates';
import { createPlan } from '../../../services/plans/createPlan';
import { getPlansList } from '../../../services/plans/listPlans';
import { removePlanFromStates } from '../../../services/plans/removePlanFromStates';
import { updatePlan } from '../../../services/plans/updatePlan';
import { getMarketList } from '../../../services/markets/getMarketList';
import { usePermissions } from '../../../hooks/usePermissions';
import {
  CreatePlanRequest,
  Insurance,
  InsurancePlan,
  UpdatePlanRequest,
} from '../../../types/plans/types';
import { State } from '../../../types/markets/types';
import { getStatesForMarketIds } from '../../../utils/getStatesForMarketIds';
import { stateOptions } from '../../../utils/stateOptions';

const PLAN_TYPES = [
  { label: 'VA plan', key: 'va' },
  { label: 'HMO', key: 'hmo' },
  { label: 'Medicare Advantage', key: 'medicareAdvantage' },
  { label: 'Commercial plan', key: 'commercial' },
  { label: 'Marketplace plan', key: 'marketplace' },
  { label: 'Primary plan', key: 'primaryPlan' },
  { label: 'Secondary plan', key: 'secondaryPlan' },
];

// Combined status options matching ProviderInsuranceTable
const COMBINED_STATUS_OPTIONS = [
  'Schedule Allowed',
  'Pending - Do Not Schedule',
  'Not Participating',
  'Established Patients Only',
  'PCP Match Required',
  'No Status',
];

// Helper function to map combined status to value (for backend)
const getValueFromCombinedStatus = (combinedStatus: string): string => {
  switch (combinedStatus) {
    case 'Schedule Allowed':
      return 'Schedule Allowed';
    case 'Pending - Do Not Schedule':
      return 'No Status';
    case 'Not Participating':
      return 'Not Participating';
    case 'Established Patients Only':
      return 'Established Patients';
    case 'PCP Match Required':
      return 'PCP Match';
    case 'No Status':
      return 'No Status';
    default:
      return 'No Status';
  }
};

// Helper function to map combined status to credential status (for backend)
const getCredentialStatusFromCombinedStatus = (
  combinedStatus: string
): string => {
  switch (combinedStatus) {
    case 'Pending - Do Not Schedule':
      return 'Pending';
    default:
      return 'Credentialed';
  }
};

interface SelectOption {
  value: string;
  label: string;
}

interface CurrentStatesDisplayProps {
  states: SelectOption[];
  onRemove: (state: string) => void;
  isLoading?: boolean;
}

const CurrentStatesDisplaySkeleton: React.FC = () => {
  return (
    <Box
      border="1px solid"
      borderColor="gray.200"
      borderRadius="0.5rem"
      bg="white"
      p="1.25rem"
      width="100%"
      boxShadow="sm"
    >
      <Flex align="center" justify="space-between" mb="1rem">
        <Skeleton height="1rem" width="120px" />
        <Skeleton height="0.75rem" width="100px" />
      </Flex>
      <Flex flexWrap="wrap" gap="0.625rem">
        {[1, 2, 3].map((i) => (
          <Skeleton
            key={i}
            height="2rem"
            width="140px"
            borderRadius="0.5rem"
          />
        ))}
      </Flex>
    </Box>
  );
};

const CurrentStatesDisplay: React.FC<CurrentStatesDisplayProps> = ({
  states,
  onRemove,
  isLoading = false,
}) => {
  if (states.length === 0) {
    return null;
  }

  return (
    <Box
      border="1px solid"
      borderColor="gray.200"
      borderRadius="0.5rem"
      bg="white"
      p="1.25rem"
      width="100%"
      boxShadow="sm"
    >
      <Flex align="center" justify="space-between" mb="1rem">
        <HStack spacing={2}>
          <Text
            fontSize="0.875rem"
            fontWeight="600"
            color="text.primary"
          >
            Current States
          </Text>
          <Box
            bg="blue.100"
            color="blue.700"
            borderRadius="full"
            px="0.625rem"
            py="0.125rem"
            fontSize="0.75rem"
            fontWeight="600"
          >
            {states.length}
          </Box>
        </HStack>
        <Text
          fontSize="0.75rem"
          color="gray.500"
          fontStyle="italic"
        >
          Click X to remove
        </Text>
      </Flex>
      <Flex flexWrap="wrap" gap="0.625rem">
        {states.map((state) => (
          <Box
            key={state.value}
            bg="blue.50"
            border="1px solid"
            borderColor="blue.200"
            borderRadius="0.5rem"
            px="0.875rem"
            py="0.5rem"
            display="flex"
            alignItems="center"
            gap="0.625rem"
            transition="all 0.2s"
            maxW="100%"
            _hover={{
              borderColor: 'blue.400',
              bg: 'blue.100',
              boxShadow: 'sm',
            }}
          >
            <Text
              fontSize="0.875rem"
              color="blue.700"
              fontWeight="500"
              flex="1"
              noOfLines={1}
              title={state.label}
              minW={0}
            >
              {state.label}
            </Text>
            <IconButton
              aria-label={`Remove ${state.label}`}
              icon={<CloseIcon />}
              size="xs"
              variant="ghost"
              colorScheme="red"
              onClick={() => onRemove(state.value)}
              isDisabled={isLoading}
              borderRadius="0.25rem"
              minW="1.5rem"
              h="1.5rem"
              flexShrink={0}
              _hover={{
                bg: 'red.100',
                color: 'red.600',
              }}
              _active={{
                bg: 'red.200',
              }}
            />
          </Box>
        ))}
      </Flex>
    </Box>
  );
};

const CreatePlan = () => {
  const { id: planId } = useParams<{ id: string }>();
  const isEditMode = Boolean(planId);
  const { isReady, permissions } = usePermissions();

  const [selectedStates, setSelectedStates] = useState<SelectOption[]>([]);
  const [initialStates, setInitialStates] = useState<string[]>([]);
  // Set when create succeeds but state assignment fails: the plan already
  // exists with no state/provider associations, so /plans/edit/:id can't
  // load it (its fetch requires at least one grid row) and /plans/:id would
  // show the same empty state. Staying on this page and keeping the id lets
  // the same form retry just the assignment, with everything already typed
  // in still there, instead of navigating to a page that can't load it.
  const [recoveryPlanId, setRecoveryPlanId] = useState<string | null>(null);
  const [planName, setPlanName] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [defaultCombinedStatus, setDefaultCombinedStatus] = useState('');
  const [planOptions, setPlanOptions] = useState(
    PLAN_TYPES.map((type) => ({
      ...type,
      value: null as null | boolean,
    }))
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [statesData, setStatesData] = useState<State[]>([]);
  const [statesLoading, setStatesLoading] = useState(true);
  const [statesLoadError, setStatesLoadError] = useState(false);
  // Set when a state-change retry fails AND the follow-up refetch (meant to
  // resync initialStates with whatever actually committed) also fails or
  // returns no row. In that case initialStates/selectedStates are left
  // stale - they must not be trusted for a further diff, since a successful
  // add followed by a failed remove could otherwise get the add repeated.
  const [committedStatesUnknown, setCommittedStatesUnknown] = useState(false);
  // Set when the plan fetch itself (not the states/market fetch) rejects or
  // returns no row for this id - editing must stay blocked in that case,
  // since every field would otherwise sit at its empty default while
  // looking fully editable.
  const [planLoadError, setPlanLoadError] = useState(false);
  const [fetchedPlanInsurance, setFetchedPlanInsurance] =
    useState<Insurance | null>(null);
  const toast = useToast();
  const navigate = useNavigate();

  // Market/state data is needed to resolve a plan's marketIds to states (see
  // getStatesForMarketIds). Fetched explicitly here rather than relying on
  // whatever another page happened to leave in localStorage - direct
  // navigation to this route never populates that cache otherwise, which
  // silently resolved every plan to zero states. A failed fetch must NOT be
  // treated the same as "loaded, genuinely zero states" - statesLoadError
  // keeps those cases distinct so an existing plan's real assignments can't
  // silently be recorded as initialStates=[] (which would make them
  // un-removable and look unassigned in the edit form).
  const loadStatesData = useCallback(() => {
    // Wait for permissions to resolve: the customer token this request
    // needs isn't available yet before isReady, so firing early can fail
    // once with no retry. The admin flag also controls whether external
    // market records come back at all - the same convention PlansList uses.
    if (!isReady) return;
    const adminFlag = permissions.isAdmin;

    setStatesLoading(true);
    setStatesLoadError(false);
    getMarketList(undefined, undefined, undefined, adminFlag)
      .then(({ data }) => {
        if (data?.states) {
          setStatesData(data.states);
        } else {
          setStatesLoadError(true);
        }
      })
      .catch((error) => {
        console.error('Failed to fetch market/state data:', error);
        setStatesLoadError(true);
      })
      .finally(() => setStatesLoading(false));
  }, [isReady, permissions.isAdmin]);

  useEffect(() => {
    loadStatesData();
  }, [loadStatesData]);

  // Re-fetches this plan's actually-committed state assignments and resyncs
  // initialStates/selectedStates from them. Used after a state-change retry
  // fails, so a further diff is computed against reality rather than the
  // pre-submit selection. Returns whether a trustworthy baseline could be
  // established - on failure or no row, initialStates is left untouched and
  // committedStatesUnknown is set so callers can block further edits rather
  // than silently falling back to a stale baseline.
  const refreshCommittedStates = useCallback(async (): Promise<boolean> => {
    const refreshed = await getPlansList(
      null,
      { insuranceIds: planId },
      10
    ).catch(() => null);
    const committedInsurance =
      refreshed?.data?.insurancePlans?.[0]?.plans?.[0]?.insurance;

    if (!committedInsurance) {
      setCommittedStatesUnknown(true);
      return false;
    }

    const committedStates = getStatesForMarketIds(
      committedInsurance.marketIds,
      statesData
    );
    setInitialStates(committedStates);
    setSelectedStates(
      committedStates
        .map((abbreviation) => {
          const option = stateOptions.find((o) => o.value === abbreviation);
          return option ? { value: option.value, label: option.label } : null;
        })
        .filter((s): s is SelectOption => s !== null)
    );
    setCommittedStatesUnknown(false);
    return true;
  }, [planId, statesData]);

  // Fetch plan data if in edit mode. isFetching only tracks whether this
  // request is in flight, not whether it actually produced a plan - a
  // rejected request or a response with no row for this id must not be
  // treated the same as "loaded successfully", or editing unlocks with
  // every field still at its empty default, and the diff below would
  // compute against initialStates=[] as if this were a brand new plan.
  const loadPlanData = useCallback(() => {
    if (!(isEditMode && planId)) return;
    setIsFetching(true);
    setPlanLoadError(false);
    getPlansList(null, { insuranceIds: planId }, 10)
      .then((res) => {
        const planData: InsurancePlan | undefined =
          res.data?.insurancePlans?.[0];
        const plan = planData?.plans?.[0];
        // planData can exist with an empty plans array (no row for this
        // plan yet) - that must not count as a successful load either, or
        // every field below fills with its empty default while
        // planLoadError stays false, unlocking an effectively blank form.
        if (!plan?.insurance) {
          setPlanLoadError(true);
          return;
        }
        setPlanName(plan.insurance.friendlyName || '');
        setEffectiveDate(plan.insurance.startDate?.slice(0, 10) || '');
        setExpiryDate(plan.insurance.endDate?.slice(0, 10) || '');
        setFetchedPlanInsurance(plan.insurance);

        setPlanOptions([
          { ...PLAN_TYPES[0], value: !!plan.insurance.va },
          { ...PLAN_TYPES[1], value: !!plan.insurance.hmo },
          {
            ...PLAN_TYPES[2],
            value: !!plan.insurance.medicareAdvantage,
          },
          {
            ...PLAN_TYPES[3],
            value: !!plan.insurance.commercial,
          },
          {
            ...PLAN_TYPES[4],
            value: !!plan.insurance.marketplace,
          },
          {
            ...PLAN_TYPES[5],
            value: !!plan.insurance.primaryPlan,
          },
          {
            ...PLAN_TYPES[6],
            value: !!plan.insurance.secondaryPlan,
          },
        ]);
      })
      .catch((error) => {
        console.error('Failed to fetch plan data:', error);
        setPlanLoadError(true);
      })
      .finally(() => setIsFetching(false));
  }, [isEditMode, planId]);

  useEffect(() => {
    loadPlanData();
  }, [loadPlanData]);

  // Derive the plan's states only once both the plan and the market/state
  // data are loaded - deriving it as soon as the plan resolves risked running
  // before statesData was populated, which silently resolved to zero states.
  // A failed states load must also skip this: recording initialStates=[] for
  // a plan that actually has assignments would make them look unassigned and
  // un-removable in the diff below.
  useEffect(() => {
    if (!fetchedPlanInsurance || statesLoading || statesLoadError) return;

    const states = getStatesForMarketIds(
      fetchedPlanInsurance.marketIds,
      statesData
    );
    setInitialStates(states);
    setSelectedStates(
      states
        .map((abbreviation) => {
          const option = stateOptions.find((o) => o.value === abbreviation);
          return option ? { value: option.value, label: option.label } : null;
        })
        .filter((s): s is SelectOption => s !== null)
    );
  }, [fetchedPlanInsurance, statesData, statesLoading, statesLoadError]);

  const handleOptionChange = (idx: number, value: boolean) => {
    setPlanOptions((prev) =>
      prev.map((opt, i) => (i === idx ? { ...opt, value } : opt))
    );
  };

  const handleRemoveState = (state: string) => {
    setSelectedStates((prev) => prev.filter((s) => s.value !== state));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // The add/remove diff below is only correct once initialStates reflects
    // the plan's real, currently-committed assignments. If the states
    // lookup failed, block submitting rather than risk computing that diff
    // against a wrong (empty) baseline.
    if (isEditMode && statesLoadError) {
      toast({
        title: 'Cannot save yet.',
        description:
          'The list of states could not be loaded, so existing assignments cannot be safely compared. Retry loading states above before saving.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    // Same reasoning, but for the case where a prior submit's state changes
    // partially failed and the follow-up resync also failed: initialStates
    // is left pointing at a baseline from before that partial success, so a
    // diff computed from it now could repeat an already-committed change.
    if (isEditMode && committedStatesUnknown) {
      toast({
        title: 'Cannot save yet.',
        description:
          "The plan's current state assignments could not be confirmed after the last save. Retry loading current states above before saving again.",
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    // Every field below is tied to the plan fetch succeeding - if it never
    // did, they're all still at their empty defaults, and submitting would
    // overwrite the real plan with blanks and diff states against [].
    if (isEditMode && planLoadError) {
      toast({
        title: 'Cannot save yet.',
        description:
          'This plan could not be loaded, so its current details and states are not available to compare against. Retry loading the plan above before saving.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    // Validation
    if (selectedStates.length === 0) {
      toast({
        title: 'State is required.',
        description: 'Please select at least one state for this insurance plan',
        status: 'error',
        duration: 2500,
        isClosable: true,
      });
      return;
    }
    if (!planName) {
      toast({
        title: 'Plan name is required.',
        status: 'error',
        duration: 2500,
        isClosable: true,
      });
      return;
    }
    if (!effectiveDate) {
      toast({
        title: 'Effective date is required.',
        status: 'error',
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    try {
      let data;
      const selectedStateValues = selectedStates.map((s) => s.value);
      const defaultCredentialStatus = defaultCombinedStatus
        ? getCredentialStatusFromCombinedStatus(defaultCombinedStatus)
        : undefined;
      const defaultValue = defaultCombinedStatus
        ? getValueFromCombinedStatus(defaultCombinedStatus)
        : undefined;

      if (isEditMode && planId) {
        // Calculate states to add and remove by comparing initial vs current selection
        const statesToAdd = selectedStateValues.filter(
          (state) => !initialStates.includes(state)
        );
        const statesToRemove = initialStates.filter(
          (state) => !selectedStateValues.includes(state)
        );

        const updatePayload: UpdatePlanRequest = {
          id: planId,
          emrInsurancePlan: planName || 'Insurance Plan',
          friendlyName: planName,
          va: planOptions[0].value === true,
          hmo: planOptions[1].value === true,
          medicareAdvantage: planOptions[2].value === true,
          commercial: planOptions[3].value === true,
          marketplace: planOptions[4].value === true,
          primaryPlan: planOptions[5].value === true,
          secondaryPlan: planOptions[6].value === true,
          startDate: effectiveDate || null,
          endDate: expiryDate || null,
        };
        ({ data } = await updatePlan(updatePayload));

        // updatePlan is already committed at this point. If a state change
        // below fails, the outer catch must not report this as a failed
        // update - the details were saved, and a retry must diff against
        // what's actually on the server now, not the stale initialStates
        // from before this submit (which would recompute the same
        // statesToAdd/statesToRemove and could miscount what still needs to
        // happen). So report the partial failure separately and refetch the
        // committed plan to resync initialStates/selectedStates before
        // letting the user try again.
        try {
          if (statesToAdd.length > 0) {
            await addPlanToStates({
              planId,
              states: statesToAdd,
              defaultCredentialStatus,
              defaultValue,
            });
          }
          if (statesToRemove.length > 0) {
            await removePlanFromStates({
              planId,
              states: statesToRemove,
            });
          }
        } catch (stateChangeError) {
          const refreshedOk = await refreshCommittedStates();
          if (refreshedOk) {
            toast({
              title: 'Plan details saved, but some state changes failed.',
              description:
                'The states shown below now reflect what actually saved. Review them and try again.',
              status: 'warning',
              duration: 6000,
              isClosable: true,
            });
          } else {
            // Don't fall back to the pre-submit initialStates here - an
            // addition from this same submit could already have committed,
            // and diffing a retry against the stale baseline would redo it.
            toast({
              title:
                'Plan details saved, but some state changes failed and the current assignments could not be confirmed.',
              description:
                'Editing states is blocked until they can be reloaded - use "Retry loading current states" below.',
              status: 'error',
              duration: 8000,
              isClosable: true,
            });
          }
          return;
        }

        toast({
          title: 'Plan updated successfully!',
          status: 'success',
          duration: 2500,
          isClosable: true,
        });
        // Navigation is handled once, by the shared `if (data.insuranceId)`
        // block below - updatePlan's response carries the same id as
        // planId, so navigating here too would push a duplicate history
        // entry and make Back appear to not leave this page.
      } else if (recoveryPlanId) {
        // A prior submit already created this plan but failed to assign
        // states - do not call createPlan again (that would duplicate the
        // plan); save any changed form details first, then retry assignment.
        try {
          await updatePlan({
            id: recoveryPlanId,
            emrInsurancePlan: planName || 'Insurance Plan',
            friendlyName: planName,
            va: planOptions[0].value === true,
            hmo: planOptions[1].value === true,
            medicareAdvantage: planOptions[2].value === true,
            commercial: planOptions[3].value === true,
            marketplace: planOptions[4].value === true,
            primaryPlan: planOptions[5].value === true,
            secondaryPlan: planOptions[6].value === true,
            startDate: effectiveDate || null,
            endDate: expiryDate || null,
          });
        } catch (updateError) {
          toast({
            title: 'Could not save plan details.',
            description:
              'The plan itself still exists. Click "Add Insurance Plan" again to retry.',
            status: 'warning',
            duration: 6000,
            isClosable: true,
          });
          return;
        }
        data = { status: 'success', insuranceId: recoveryPlanId };
        try {
          await addPlanToStates({
            planId: recoveryPlanId,
            states: selectedStateValues,
            defaultCredentialStatus,
            defaultValue,
          });
          setRecoveryPlanId(null);
        } catch (stateAssignError) {
          toast({
            title: 'Still unable to assign states.',
            description:
              'The plan itself is already saved. Click "Add Insurance Plan" again to retry.',
            status: 'warning',
            duration: 6000,
            isClosable: true,
          });
          return;
        }

        toast({
          title: 'Plan created successfully!',
          status: 'success',
          duration: 2500,
          isClosable: true,
        });
      } else {
        const createPayload: CreatePlanRequest = {
          emrInsurancePlan: planName || 'Insurance Plan',
          friendlyName: planName,
          va: planOptions[0].value === true,
          hmo: planOptions[1].value === true,
          medicareAdvantage: planOptions[2].value === true,
          commercial: planOptions[3].value === true,
          marketplace: planOptions[4].value === true,
          primaryPlan: planOptions[5].value === true,
          secondaryPlan: planOptions[6].value === true,
          startDate: effectiveDate || null,
          endDate: expiryDate || null,
        };
        ({ data } = await createPlan(createPayload));

        if (data.insuranceId) {
          // The plan record is already committed at this point. If state
          // assignment fails here, the outer catch must not report this as
          // a failed create - the plan exists, so retrying the form must not
          // call createPlan again. Neither /plans/edit/:id nor /plans/:id
          // can load a plan with no state/provider associations yet (both
          // fetch by provider/practice grid rows, and there are none until
          // assignment succeeds), so stay on this page and keep the id
          // instead of navigating anywhere.
          try {
            await addPlanToStates({
              planId: data.insuranceId,
              states: selectedStateValues,
              defaultCredentialStatus,
              defaultValue,
            });
          } catch (stateAssignError) {
            setRecoveryPlanId(data.insuranceId);
            toast({
              title: 'Plan created, but state assignment failed.',
              description:
                'The plan was saved without any states assigned. Click "Add Insurance Plan" again to retry - it will not create a duplicate.',
              status: 'warning',
              duration: 7000,
              isClosable: true,
            });
            return;
          }
        }

        toast({
          title: 'Plan created successfully!',
          status: 'success',
          duration: 2500,
          isClosable: true,
        });
      }
      if (data.insuranceId) {
        navigate(`/plans/${data.insuranceId}`);
      }
    } catch (error) {
      toast({
        title: isEditMode
          ? 'Failed to update plan.'
          : 'Failed to create plan.',
        status: 'error',
        duration: 2500,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Both inputs the initialization effect above needs - the plan fetch and
  // the market/state fetch - must finish (successfully) before the selector
  // is safe to touch in edit mode. Otherwise a selection made while either is
  // still loading gets silently overwritten once that effect runs.
  const statesSelectorLocked =
    isEditMode &&
    (isFetching || statesLoading || statesLoadError || committedStatesUnknown);

  return (
    <Flex
      direction="column"
      align="flex-start"
      gap="1rem"
      width="60rem"
      p={6}
      mx="auto"
    >
      <Text
        fontSize="1.875rem"
        fontWeight="700"
        lineHeight={'133%'}
        color="text.primary"
        mb="2rem"
      >
        {isEditMode ? 'Edit Insurance Plan' : 'Add Insurance plan'}
      </Text>
      {isEditMode && planLoadError && !isFetching ? (
        <Box
          border="1px solid"
          borderColor="red.200"
          bg="red.50"
          borderRadius="0.5rem"
          p="1.5rem"
          width="100%"
        >
          <Text fontSize="1rem" fontWeight="600" color="red.700">
            This plan could not be loaded.
          </Text>
          <Text fontSize="0.875rem" color="red.600" mt="0.5rem">
            Its details and current states aren't available, so editing is
            blocked until it loads - saving now could overwrite the real
            plan with blank fields.
          </Text>
          <Button
            size="sm"
            colorScheme="red"
            variant="outline"
            mt="1rem"
            onClick={loadPlanData}
          >
            Retry loading plan
          </Button>
        </Box>
      ) : (
      <form onSubmit={handleSubmit} style={{ width: '100%' }}>
        <Flex direction="column" gap="1.5rem">
          {!isEditMode && recoveryPlanId && (
            <Box
              border="1px solid"
              borderColor="orange.200"
              bg="orange.50"
              borderRadius="0.5rem"
              p="1rem"
              width="100%"
            >
              <Text fontSize="0.875rem" fontWeight="600" color="orange.700">
                This plan was already saved, but assigning states failed.
              </Text>
              <Text fontSize="0.8125rem" color="orange.600" mt="0.25rem">
                Nothing here will create a duplicate. Click "Add Insurance
                Plan" below to retry assigning the selected states to the
                same plan.
              </Text>
            </Box>
          )}
          {isEditMode && statesLoadError && !statesLoading && (
            <Box
              border="1px solid"
              borderColor="red.200"
              bg="red.50"
              borderRadius="0.5rem"
              p="1rem"
              width="100%"
            >
              <Text fontSize="0.875rem" fontWeight="600" color="red.700">
                Could not load the list of states.
              </Text>
              <Text fontSize="0.8125rem" color="red.600" mt="0.25rem">
                This plan's existing state assignments cannot be shown or
                changed safely until this loads. Saving is disabled until you
                retry.
              </Text>
              <Button
                size="sm"
                colorScheme="red"
                variant="outline"
                mt="0.75rem"
                onClick={loadStatesData}
              >
                Retry loading states
              </Button>
            </Box>
          )}
          {isEditMode && committedStatesUnknown && (
            <Box
              border="1px solid"
              borderColor="red.200"
              bg="red.50"
              borderRadius="0.5rem"
              p="1rem"
              width="100%"
            >
              <Text fontSize="0.875rem" fontWeight="600" color="red.700">
                Could not confirm this plan's current state assignments.
              </Text>
              <Text fontSize="0.8125rem" color="red.600" mt="0.25rem">
                A previous save partially failed, and reloading what actually
                saved also failed. Editing states is disabled until this
                reloads, so a retry can't repeat work that already
                succeeded.
              </Text>
              <Button
                size="sm"
                colorScheme="red"
                variant="outline"
                mt="0.75rem"
                onClick={refreshCommittedStates}
              >
                Retry loading current states
              </Button>
            </Box>
          )}
          {/* State Selection - Single multi-select for both create and edit modes */}
          {(() => {
            const shouldShowSkeleton =
              isFetching || (isEditMode && statesLoading);

            if (shouldShowSkeleton) {
              return <CurrentStatesDisplaySkeleton />;
            }

            if (isEditMode && (statesLoadError || committedStatesUnknown)) {
              return null;
            }

            if (selectedStates.length > 0) {
              return (
                <CurrentStatesDisplay
                  states={selectedStates}
                  onRemove={handleRemoveState}
                  isLoading={isLoading}
                />
              );
            }

            return null;
          })()}
          <FormControl
            mt={selectedStates.length > 0 ? '1.5rem' : '0'}
          >
            <FormLabel
              fontSize="0.875rem"
              fontWeight="500"
              lineHeight="1.25rem"
              color="text.secondary"
              mb="0.5rem"
            >
              {selectedStates.length > 0
                ? 'Add More States'
                : 'Select States'}
              *
            </FormLabel>
            <ReactSelect
              isMulti
              isSearchable
              isClearable={false}
              isDisabled={statesSelectorLocked}
              options={stateOptions}
              value={selectedStates}
              onChange={(newValue: MultiValue<SelectOption>) => {
                setSelectedStates(newValue as SelectOption[]);
              }}
              placeholder={
                selectedStates.length > 0
                  ? 'Search to add more states...'
                  : 'Search and select states...'
              }
              noOptionsMessage={() => 'No states found'}
              styles={{
                control: (base, state) => ({
                  ...base,
                  borderColor: state.isFocused ? '#3182ce' : '#E2E8F0',
                  minHeight: '2.5rem',
                  boxShadow: state.isFocused
                    ? '0 0 0 1px #3182ce'
                    : 'none',
                  '&:hover': {
                    borderColor: '#3182ce',
                  },
                }),
                placeholder: (base) => ({
                  ...base,
                  color: '#718096',
                  fontSize: '0.875rem',
                }),
                input: (base) => ({
                  ...base,
                  fontSize: '0.875rem',
                }),
                menu: (base) => ({
                  ...base,
                  zIndex: 9999,
                }),
                option: (base, state) => ({
                  ...base,
                  fontSize: '0.875rem',
                  backgroundColor: state.isSelected
                    ? '#E6FFFA'
                    : state.isFocused
                    ? '#EBF8FF'
                    : 'white',
                  color: state.isSelected ? '#234E52' : '#2D3748',
                  '&:active': {
                    backgroundColor: '#BEE3F8',
                  },
                }),
                multiValue: (base) => ({
                  ...base,
                  display: 'none', // Hide chips since we show them in Current States above
                }),
                valueContainer: (base) => ({
                  ...base,
                  paddingLeft: selectedStates.length > 0 ? '0.5rem' : '0.75rem',
                }),
                indicatorsContainer: (base) => ({
                  ...base,
                  paddingRight: '0.5rem',
                }),
              }}
            />
            <Text
              fontSize="0.75rem"
              color="gray.600"
              mt="0.5rem"
              fontStyle="italic"
            >
              {selectedStates.length > 0
                ? 'Selected states are shown above. Use the search to add more states.'
                : isEditMode
                ? 'Search and select states to add them to this plan'
                : 'This insurance plan will be applied to every practice and provider in the selected states'}
            </Text>
          </FormControl>

          <FormControl>
            <FormLabel
              fontSize="0.875rem"
              fontWeight="500"
              lineHeight="1.25rem"
              color="text.secondary"
              mb="0.5rem"
            >
              Plan name *
            </FormLabel>
            {isFetching ? (
              <Skeleton height="2.5rem" borderRadius="0.375rem" />
            ) : (
              <Input
                placeholder="Add plan name"
                value={planName}
                onChange={(e) => setPlanName(e.target.value)}
                size="md"
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="0.375rem"
                _focus={{
                  borderColor: 'blue.500',
                  boxShadow: '0 0 0 1px #3182ce',
                }}
              />
            )}
          </FormControl>

          <HStack spacing={4} align="flex-start">
            <FormControl>
              <FormLabel
                fontSize="0.875rem"
                fontWeight="500"
                lineHeight="1.25rem"
                color="text.secondary"
                mb="0.5rem"
              >
                Effective date *
              </FormLabel>
              {isFetching ? (
                <Skeleton height="2.5rem" borderRadius="0.375rem" />
              ) : (
                <Input
                  type="date"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  size="md"
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="0.375rem"
                  _focus={{
                    borderColor: 'blue.500',
                    boxShadow: '0 0 0 1px #3182ce',
                  }}
                />
              )}
            </FormControl>
            <FormControl>
              <FormLabel
                fontSize="0.875rem"
                fontWeight="500"
                lineHeight="1.25rem"
                color="text.secondary"
                mb="0.5rem"
              >
                Expiry date
              </FormLabel>
              {isFetching ? (
                <Skeleton height="2.5rem" borderRadius="0.375rem" />
              ) : (
                <Input
                  type="date"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                  size="md"
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="0.375rem"
                  _focus={{
                    borderColor: 'blue.500',
                    boxShadow: '0 0 0 1px #3182ce',
                  }}
                />
              )}
            </FormControl>
          </HStack>

          {/* Default Values Section - Show when adding states */}
          {(() => {
            if (!isEditMode) {
              return selectedStates.length > 0;
            }
            const selectedStateValues = selectedStates.map((s) => s.value);
            const newStates = selectedStateValues.filter(
              (state) => !initialStates.includes(state)
            );
            return newStates.length > 0;
          })() && (
            <Box
              border="1px solid"
              borderColor="gray.200"
              borderRadius="0.75rem"
              bg="blue.50"
              mt="1rem"
              p="1rem"
              width="100%"
            >
              <Text
                fontSize="0.875rem"
                fontWeight="600"
                color="blue.700"
                mb="0.75rem"
              >
                Default Values for Providers in Selected States
              </Text>
              <Text
                fontSize="0.75rem"
                color="blue.600"
                mb="1rem"
                fontStyle="italic"
              >
                These values apply to every provider in the selected states
                until real credentialing data is recorded for them
              </Text>
              <Flex direction="column" gap="1rem">
                <FormControl>
                  <FormLabel
                    fontSize="0.875rem"
                    fontWeight="500"
                    lineHeight="1.25rem"
                    color="text.secondary"
                    mb="0.5rem"
                  >
                    Default Status
                  </FormLabel>
                  <Select
                    placeholder="Select default status"
                    value={defaultCombinedStatus}
                    onChange={(e) => setDefaultCombinedStatus(e.target.value)}
                    size="md"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="0.375rem"
                    _focus={{
                      borderColor: 'blue.500',
                      boxShadow: '0 0 0 1px #3182ce',
                    }}
                  >
                    {COMBINED_STATUS_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Select>
                  <Text
                    fontSize="0.75rem"
                    color="gray.500"
                    mt="0.5rem"
                    fontStyle="italic"
                  >
                    This combines credential status and patient acceptance into
                    a single selection
                  </Text>
                </FormControl>
              </Flex>
            </Box>
          )}

          <Box
            border="1px solid"
            borderColor="gray.200"
            borderRadius="0.75rem"
            bg="white"
            mt="1rem"
            p="1rem"
            width="100%"
            boxShadow="sm"
          >
            {planOptions.map((opt, idx) => (
              <Flex
                key={opt.key}
                align="center"
                justify="space-between"
                py={3}
                borderBottom={
                  idx !== planOptions.length - 1
                    ? '1px solid #EDF2F7'
                    : 'none'
                }
              >
                <Text
                  fontWeight="500"
                  fontSize="0.875rem"
                  color="text.secondary"
                >
                  {opt.label}
                </Text>
                <HStack spacing={2}>
                  <Button
                    size="sm"
                    height="2.5rem"
                    px="1.5rem"
                    bg={opt.value === true ? 'green.500' : 'gray.100'}
                    color={opt.value === true ? 'white' : 'green.600'}
                    border="1px solid"
                    borderColor={
                      opt.value === true ? 'green.500' : 'gray.200'
                    }
                    borderRadius="0.5rem"
                    fontWeight="500"
                    _hover={{
                      bg:
                        opt.value === true ? 'green.600' : 'gray.200',
                      borderColor:
                        opt.value === true ? 'green.600' : 'gray.300',
                    }}
                    _active={{
                      bg:
                        opt.value === true ? 'green.700' : 'gray.300',
                    }}
                    onClick={() => handleOptionChange(idx, true)}
                    transition="all 0.2s"
                  >
                    <HStack spacing={1}>
                      <Text fontSize="1rem">✓</Text>
                      <Text>Yes</Text>
                    </HStack>
                  </Button>
                  <Button
                    size="sm"
                    height="2.5rem"
                    px="1.5rem"
                    bg={opt.value === false ? 'red.500' : 'gray.100'}
                    color={opt.value === false ? 'white' : 'red.600'}
                    border="1px solid"
                    borderColor={
                      opt.value === false ? 'red.500' : 'gray.200'
                    }
                    borderRadius="0.5rem"
                    fontWeight="500"
                    _hover={{
                      bg:
                        opt.value === false ? 'red.600' : 'gray.200',
                      borderColor:
                        opt.value === false ? 'red.600' : 'gray.300',
                    }}
                    _active={{
                      bg:
                        opt.value === false ? 'red.700' : 'gray.300',
                    }}
                    onClick={() => handleOptionChange(idx, false)}
                    transition="all 0.2s"
                  >
                    <HStack spacing={1}>
                      <Text fontSize="1rem">✕</Text>
                      <Text>No</Text>
                    </HStack>
                  </Button>
                </HStack>
              </Flex>
            ))}
          </Box>

          <Button
            colorScheme="blue"
            mt="2rem"
            width="200px"
            alignSelf="flex-start"
            type="submit"
            height="3rem"
            borderRadius="0.375rem"
            background="blue.500"
            color="white"
            fontWeight="600"
            isLoading={isLoading}
            loadingText="Saving..."
            _hover={{
              bg: 'blue.600',
            }}
            _active={{
              bg: 'blue.700',
            }}
          >
            {isEditMode
              ? 'Save Insurance Plan'
              : 'Add Insurance Plan'}
          </Button>
        </Flex>
      </form>
      )}
    </Flex>
  );
};

export default CreatePlan;
